import React, { memo, useCallback } from 'react';
import equal from 'fast-deep-equal';
import { motion } from 'motion/react';

import { HomeFeedKeyPointTier, type SkimTier } from '@repo/types';
import { cn } from '@repo/ui/lib/utils';

import { YtVideosArrayData } from '@/hooks/api/yt/model';
import { usePostMessage } from '@/providers/post-message-provider';

import { YtVideoCardActionButtons } from './yt-video-card-action-buttons';
import { YtVideoCardAnalysisWrapper } from './yt-video-card-analysis/yt-video-card-analysis-wrapper';
import { YtVideoCardContent } from './yt-video-card-content';
import { YtVideoCardHeader } from './yt-video-card-header';

interface YtVideoCardProps {
  ytData: YtVideosArrayData;
  className?: string;
  isShared?: boolean;
}

/**
 * Canonical tier for the card's SKIM CTA: short is the canonical skim
 * duration; fall back to the shallowest present tier when short is absent.
 *
 * Returns the key alongside the tier because the card sends BOTH to the
 * analysis — the counts it renders and the tier playback opens on have to be
 * the same choice, so they are made once, here.
 */
const pickSkimTier = (
  ytData: YtVideosArrayData,
): { key: SkimTier; tier: HomeFeedKeyPointTier } | undefined => {
  const byType = ytData.videoAnalysis?.keyPointsByType;
  if (byType?.short) return { key: 'short', tier: byType.short };
  if (byType?.medium) return { key: 'medium', tier: byType.medium };
  if (byType?.relaxed) return { key: 'relaxed', tier: byType.relaxed };
  return undefined;
};

const YtVideoCardChild: React.FC<YtVideoCardProps> = ({
  ytData,
  className,
}) => {
  const { sendOpenVideoWithSkim } = usePostMessage();

  const skim = pickSkimTier(ytData);
  const skimTierKey = skim?.key;

  const redirectToVideo = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const shouldOpenInNewTab =
        e.ctrlKey || // Ctrl+click on Windows/Linux
        e.metaKey || // Cmd+click on macOS
        e.shiftKey || // Shift+click (opens in new window/tab)
        e.button === 1; // Middle mouse button click

      const shouldSendTheMessage = e.button === 0 || shouldOpenInNewTab;
      if (!shouldSendTheMessage) return;

      // Opening the video also starts its skim on the host page, the way the
      // website's cards do — the extension carries the intent across the
      // navigation.
      sendOpenVideoWithSkim({
        url: ytData.url,
        isNewTab: shouldOpenInNewTab,
        intent: {
          videoId: ytData.youtubeId,
          ...(skimTierKey ? { tier: skimTierKey } : {}),
          source: 'yt_video_card',
        },
      });
    },
    [ytData.url, ytData.youtubeId, skimTierKey, sendOpenVideoWithSkim],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.3,
      }}
      role="link"
      title={ytData.url}
      onMouseDown={redirectToVideo}
      className={cn(
        'flex flex-1 cursor-pointer flex-col justify-between gap-1 overflow-hidden rounded-[4px] bg-white pt-1 transition-[color,background-color,box-shadow,border-color,border-width] duration-200 hover:shadow-[0px_0px_6px_0px_rgba(0,0,0,0.3)] cred-md:gap-1.5 cred-md:pt-1.5 cred-lg:pt-[7px] cred-xl:pt-2 data-[state=open]:shadow-[0px_0px_6px_0px_rgba(0,0,0,0.3)]',
        className,
      )}
    >
      <YtVideoCardHeader
        youtubeId={ytData.youtubeId}
        uploadDate={ytData.createdAt}
        channelHandle={ytData.channel.channelHandle}
        channelName={ytData.channel.name}
        url={ytData.url}
      />

      <YtVideoCardContent
        title={ytData.title}
        videoThumbnail={ytData.thumbnailUrl}
        channelThumbnail={ytData.channel.chanelProfilePictureUrl}
        channelName={ytData.channel?.name}
        duration={ytData.duration ?? 0}
        viewCount={ytData.viewCount}
        credibilityScore={ytData.videoAnalysis?.metrics?.credibilityScore ?? 0}
      />

      <YtVideoCardActionButtons
        keyMomentsDuration={skim?.tier.totalDurationSeconds ?? 0}
        keyClaimsCount={skim?.tier.count ?? 0}
        // onSkimClick={() =>
        //   setIntent({ openSection: 'skim', autoPlaySkim: true })
        // }
        // onKeyPointsClick={() =>
        //   setIntent({ openSection: 'key-points', scrollToKeyPoints: true })
        // }
      />
    </motion.div>
  );
};

const PureYtVideoCard: React.FC<YtVideoCardProps> = (props) => {
  return (
    <YtVideoCardAnalysisWrapper
      videoId={props.ytData.youtubeId}
      isShared={props.isShared ?? false}
    >
      <YtVideoCardChild {...props} />
    </YtVideoCardAnalysisWrapper>
  );
};

export const YtVideoCard = memo(PureYtVideoCard, (prevProps, nextProps) => {
  if (prevProps.className !== nextProps.className) {
    return false;
  }
  if (prevProps.isShared !== nextProps.isShared) {
    return false;
  }
  if (!equal(prevProps.ytData, nextProps.ytData)) {
    return false;
  }

  return true;
});
